import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api } from "../api";
import { useAuth } from "./AuthContext";

const FavoritesContext = createContext(null);

export function FavoritesProvider({ children }) {
  const { token, profile } = useAuth();
  const [ids, setIds] = useState(() => new Set());
  const [loading, setLoading] = useState(false);

  const reload = useCallback(async () => {
    if (!token || !profile?.id) {
      setIds(new Set());
      return [];
    }
    setLoading(true);
    try {
      const data = await api("/api/favorites", { token });
      const next = new Set((data.videos || []).map((v) => v.id));
      setIds(next);
      return data.videos || [];
    } finally {
      setLoading(false);
    }
  }, [token, profile?.id]);

  useEffect(() => {
    reload().catch(() => setIds(new Set()));
  }, [reload]);

  const value = useMemo(
    () => ({
      favoriteIds: ids,
      loading,
      isFavorite(videoId) {
        return ids.has(Number(videoId));
      },
      reload,
      async toggleFavorite(videoId) {
        if (!token || !profile?.id) {
          throw new Error("Selecciona un perfil");
        }
        const id = Number(videoId);
        const currently = ids.has(id);
        // Optimistic update
        setIds((prev) => {
          const next = new Set(prev);
          if (currently) next.delete(id);
          else next.add(id);
          return next;
        });
        try {
          if (currently) {
            await api(`/api/videos/${id}/favorite`, { method: "DELETE", token });
            return false;
          }
          await api(`/api/videos/${id}/favorite`, { method: "POST", token });
          return true;
        } catch (err) {
          // Revert
          setIds((prev) => {
            const next = new Set(prev);
            if (currently) next.add(id);
            else next.delete(id);
            return next;
          });
          throw err;
        }
      },
    }),
    [ids, loading, token, profile?.id, reload]
  );

  return <FavoritesContext.Provider value={value}>{children}</FavoritesContext.Provider>;
}

export function useFavorites() {
  const ctx = useContext(FavoritesContext);
  if (!ctx) {
    return {
      favoriteIds: new Set(),
      loading: false,
      isFavorite: () => false,
      reload: async () => [],
      toggleFavorite: async () => false,
    };
  }
  return ctx;
}
